/**
 * Lecture d'un fichier de saisies — CSV ou XLSX — jusqu'à des périodes prêtes à envoyer.
 *
 * Même découpage que `lireTableur`, dont ce module réutilise tout ce qui ne dépend pas des
 * colonnes : `lireFichier`, `normaliserEntete` et `enDateIso`. Seule l'analyse des lignes est
 * propre aux saisies, et elle est **pure** — des tableaux entrent, des périodes validées sortent.
 * C'est la partie qui se trompe, donc celle qu'on vérifie.
 *
 * **Une ligne d'exemple ne peut pas s'auto-nommer ici.** Dans un fichier de notes, on écrit
 * « EXEMPLE à supprimer » dans le contenu et personne ne s'y trompe. Ici toutes les colonnes sont
 * des dates ou des nombres : une ligne du modèle oubliée s'importerait comme une vraie saisie et
 * **écraserait** la période correspondante. D'où la granularité `exemple`, reconnue, affichée et
 * structurellement impossible à importer.
 */

import { normaliserEntete, enDateIso, lireFichier, EXTENSIONS } from './lireTableur';

export { lireFichier, EXTENSIONS };

export const COLONNES_REQUISES = ['granularite', 'debut'];

/**
 * Jeux de colonnes, un par domaine de la modale.
 *
 * La modale est organisée par **domaine** — back-office, objectifs, coûts — et non par mode :
 * « Saisie » et « Import » sont deux façons de faire la même chose, pas deux choses. Chaque
 * domaine a donc son propre fichier, avec ses seules colonnes : proposer un modèle à six
 * colonnes pour n'en remplir que deux invite à remplir les quatre autres, et l'import
 * effacerait alors le domaine d'à côté.
 */
export const JEUX = {
  backOffice: {
    valeurs: [
      { colonne: 'transactions_bo', cle: 'boTransactions', titre: 'transactions BO', entier: true },
      { colonne: 'ca_bo', cle: 'boRevenue', titre: 'CA BO', entier: false }
    ],
    exemples: ['exemple;01/01/2026;240;30150,40', 'exemple;05/01/2026;58;7210,00']
  },
  objectifs: {
    valeurs: [
      { colonne: 'objectif_ca', cle: 'objRevenue', titre: 'objectif de CA', entier: false },
      { colonne: 'objectif_transactions', cle: 'objTransactions', titre: 'objectif de transactions', entier: true }
    ],
    exemples: ['exemple;01/01/2026;30000;250', 'exemple;01/02/2026;28000;235']
  },
  /*
   * Coûts et budgets portent une colonne de plus : la **destination**.
   *
   * Un back-office décrit le site entier, un coût décrit un levier — le SEO, Google Ads, une
   * prestation. La période seule ne suffit donc pas à désigner la ligne à écrire, et un fichier
   * sans destination obligerait à importer une fois par levier.
   *
   * Elle est déclarée dans `COLONNES_REQUISES_PAR_CIBLE` plutôt qu'en valeur : c'est une clé,
   * au même titre que la granularité et la date, pas un chiffre à écrire.
   */
  couts: {
    parCible: true,
    valeurs: [
      { colonne: 'montant', cle: 'montant', titre: 'montant', entier: false }
    ],
    exemples: ['exemple;seo;01/01/2026;2500', 'exemple;googleAds;01/02/2026;2500']
  },
  budget: {
    parCible: true,
    valeurs: [
      { colonne: 'montant', cle: 'montant', titre: 'montant', entier: false }
    ],
    exemples: ['exemple;seo;01/01/2026;3000', 'exemple;googleAds;01/02/2026;12000']
  }
};

/** La destination s'insère entre la granularité et la date : on lit « quoi, où, quand ». */
const COLONNE_CIBLE = 'destination';

/** Les colonnes d'un jeu, dans l'ordre du modèle. */
export const colonnesDuJeu = (jeu) => [
  'granularite',
  ...(JEUX[jeu].parCible ? [COLONNE_CIBLE] : []),
  'debut',
  ...JEUX[jeu].valeurs.map(v => v.colonne)
];

/** Les colonnes sans lesquelles une ligne ne désigne rien, selon le jeu. */
const colonnesRequises = (jeu) => (
  JEUX[jeu].parCible ? [...COLONNES_REQUISES, COLONNE_CIBLE] : COLONNES_REQUISES
);

/** Conservé pour l'appelant historique : le jeu complet back-office + objectifs. */
export const COLONNES = [
  'granularite', 'debut', 'transactions_bo', 'ca_bo', 'objectif_ca', 'objectif_transactions'
];

/** Aligné sur le plafond du serveur, pour que le refus se voie avant l'envoi et non après. */
export const LIGNES_MAX = 1000;

const GRANULARITES = {
  mois: 'MOIS', mensuel: 'MOIS',
  semaine: 'SEMAINE', hebdo: 'SEMAINE', hebdomadaire: 'SEMAINE',
  jour: 'JOUR', quotidien: 'JOUR', journalier: 'JOUR'
};



/**
 * Un nombre écrit en français : virgule décimale, espaces de milliers, symbole monétaire toléré.
 *
 * Rend `null` pour une cellule vide et `NaN` pour ce qui n'est pas un nombre — deux cas qu'il
 * faut distinguer, l'un étant une absence légitime et l'autre une faute à signaler.
 */
export function enNombre(cellule) {
  if (cellule === null || cellule === undefined) return null;
  if (typeof cellule === 'number') return cellule;
  const propre = String(cellule).replace(/[\s  €%]/g, '').replace(',', '.');
  if (propre === '') return null;
  const n = Number(propre);
  return Number.isFinite(n) ? n : NaN;
}

/** Bornes exigées par le serveur : un mois commence un 1er, une semaine un lundi. */
function alignement(granularite, dateIso) {
  const d = new Date(`${dateIso}T00:00:00Z`);
  if (granularite === 'MOIS' && d.getUTCDate() !== 1) {
    const premier = `${dateIso.slice(0, 7)}-01`;
    return `une saisie mensuelle commence un 1er de mois — indiquez ${premier}`;
  }
  if (granularite === 'SEMAINE' && d.getUTCDay() !== 1) {
    const lundi = new Date(d);
    lundi.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
    return `une saisie hebdomadaire commence un lundi — indiquez ${lundi.toISOString().slice(0, 10)}`;
  }
  return null;
}

/**
 * Analyse les lignes d'une feuille et rend ce qui est importable, ce qui ne l'est pas, et pourquoi.
 *
 * Rien n'est jamais écarté en silence : une ligne fautive ressort avec son numéro et sa raison,
 * pour que l'écran les montre toutes d'un coup plutôt qu'une par tentative.
 */
export function analyserLignes(entetes, lignes, jeu = 'backOffice', cibles = null) {
  const VALEURS = JEUX[jeu].valeurs;
  const attendues = colonnesDuJeu(jeu);
  const parCible = Boolean(JEUX[jeu].parCible);

  /**
   * Le catalogue des destinations, indexé par clé **et** par intitulé.
   *
   * Personne ne tape `socialOrganique` dans un tableur : on y écrit « Réseaux sociaux ». Les
   * deux écritures sont donc acceptées, et la comparaison ignore casse et accents — c'est
   * exactement ce que fait `normaliserEntete` pour les en-têtes.
   *
   * Sans catalogue — l'appelant ne l'a pas encore chargé —, aucune destination n'est validée :
   * on refuse alors toutes les lignes plutôt que d'en écrire sous une clé inventée, qu'aucun
   * bloc du tableau ne relirait jamais.
   */
  const parCle = new Map();
  for (const cible of cibles || []) {
    parCle.set(normaliserEntete(cible.cle), cible.cle);
    if (cible.titre) parCle.set(normaliserEntete(cible.titre), cible.cle);
  }

  const index = {};
  (entetes || []).forEach((brut, i) => {
    const nom = normaliserEntete(brut);
    if (attendues.includes(nom)) index[nom] = i;
  });

  // Une colonne de valeur absente n'est pas une faute — on peut n'importer qu'un des deux
  // chiffres du domaine — mais toutes absentes le sont : il n'y aurait rien à écrire.
  const manquantes = colonnesRequises(jeu).filter(c => !(c in index));
  if (manquantes.length === 0 && VALEURS.every(v => !(v.colonne in index))) {
    manquantes.push(...VALEURS.map(v => v.colonne));
  }
  if (manquantes.length > 0) {
    return { colonnesManquantes: manquantes, lignes: [] };
  }

  const vues = new Set();

  const analysees = (lignes || []).slice(0, LIGNES_MAX).map((cellules, rang) => {
    const numero = rang + 2; // +1 pour l'en-tête, +1 pour compter à partir de 1 comme le tableur
    const lire = (colonne) => (colonne in index ? cellules[index[colonne]] : null);

    const granulariteBrute = normaliserEntete(lire('granularite') ?? '');
    if (granulariteBrute === 'exemple') {
      return { numero, exemple: true, faute: 'ligne d\'exemple du modèle — non importée' };
    }

    const granularite = GRANULARITES[granulariteBrute];
    if (!granularite) {
      return { numero, faute: `granularite : « ${lire('granularite')} » inconnue (mois, semaine ou jour)` };
    }

    const debut = enDateIso(lire('debut'));
    if (!debut) {
      return { numero, faute: `debut : « ${lire('debut')} » n'est pas une date lisible` };
    }

    const desalignee = alignement(granularite, debut);
    if (desalignee) return { numero, granularite, debut, faute: `debut : ${desalignee}` };

    let cible = null;
    if (parCible) {
      const brute = lire(COLONNE_CIBLE);
      cible = parCle.get(normaliserEntete(brute ?? ''));
      if (!cible) {
        return {
          numero,
          granularite,
          debut,
          faute: parCle.size === 0
            ? 'destination : catalogue indisponible, import impossible'
            : `destination : « ${brute} » inconnue`
        };
      }
    }

    const valeurs = {};
    for (const { colonne, cle, titre, entier } of VALEURS) {
      const n = enNombre(lire(colonne));
      if (Number.isNaN(n)) return { numero, faute: `${titre} : « ${lire(colonne)} » n'est pas un nombre` };
      if (n !== null && n < 0) return { numero, faute: `${titre} : une valeur négative n'est pas acceptée` };
      if (n !== null && entier && !Number.isInteger(n)) return { numero, faute: `${titre} : nombre entier attendu` };
      valeurs[cle] = n;
    }

    // Une ligne sans aucun chiffre supprimerait la période côté serveur, par la règle « toutes
    // les valeurs nulles = retrait ». Importer un fichier ne doit jamais effacer par distraction.
    if (Object.values(valeurs).every(v => v === null)) {
      return { numero, granularite, debut, faute: 'la ligne ne porte aucun chiffre' };
    }

    // La destination entre dans la clé d'unicité : le même mois revient légitimement pour le
    // SEO puis pour Google Ads, ce n'est pas un doublon.
    const cle = parCible ? `${cible}|${granularite}|${debut}` : `${granularite}|${debut}`;
    if (vues.has(cle)) {
      return {
        numero,
        granularite,
        debut,
        faute: parCible
          ? 'même destination et même période déjà présentes plus haut dans le fichier'
          : 'période déjà présente plus haut dans le fichier'
      };
    }
    vues.add(cle);

    return { numero, granularite, debut, ...(parCible ? { cible } : {}), ...valeurs };
  });

  return { colonnesManquantes: [], lignes: analysees, tronque: (lignes || []).length > LIGNES_MAX };
}

/**
 * Modèle CSV téléchargeable.
 *
 * BOM et point-virgule : sans le premier, Excel en français lit de l'ISO-8859-1 et abîme les
 * accents ; sans le second, il range toute la ligne dans la colonne A.
 */
export function modeleCsv(jeu = 'backOffice', cibles = null) {
  /*
   * **Pour un jeu par destination, le modèle liste toutes les destinations.**
   *
   * Deux lignes d'exemple obligeaient à deviner les clés — `socialOrganique`, `googleAds` — et
   * à les écrire à la main, sans jamais savoir lesquelles existent sur cette propriété. Le
   * fichier devient une grille prête à remplir : une ligne par levier, dans l'ordre du
   * catalogue, le montant à compléter.
   *
   * La granularité reste `exemple` sur chacune. Cette valeur est **refusée à l'import** : rien
   * ne part tant qu'on ne l'a pas remplacée par « mois », « semaine » ou « jour » — et l'on ne
   * remplace que les lignes qu'on veut vraiment envoyer. Les autres s'ignorent d'elles-mêmes,
   * sans avoir à les supprimer.
   */
  if (JEUX[jeu].parCible && (cibles || []).length > 0) {
    const vides = JEUX[jeu].valeurs.map(() => '').join(';');
    const lignes = [
      colonnesDuJeu(jeu).join(';'),
      ...cibles.map(c => `exemple;${c.cle};${PREMIER_DU_MOIS()};${vides}`)
    ];
    return `﻿${lignes.join('\r\n')}\r\n`;
  }

  const lignes = [colonnesDuJeu(jeu).join(';'), ...JEUX[jeu].exemples];
  return `﻿${lignes.join('\r\n')}\r\n`;
}

/** Le 1er du mois en cours, au format que le modèle attend : une date déjà alignée. */
const PREMIER_DU_MOIS = () => {
  const d = new Date();
  return `01/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};
